import ContentfulContent from "@cms/ContentfulContent"
import Button from "@components/buttons/Button"
import Helmet from "@components/Helmet"
import Layout from "@components/Layout"
import ProductBlockWithSwatches, {
  Container as ProductBlockContainer,
} from "@components/ProductBlockWithSwatches"
import ProductFilterMobile from "@components/ProductFilterMobile"
import SortDropdown, { SortContainer } from "@components/SortDropdown"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { SELECTED_PRODUCTS_KEY } from "@constants/storageKeys"
import {
  h4Styles,
  h5Styles,
  smallcapsBold13Styles,
} from "@constants/typography"
import useElevarEvents from "@hooks/useElevarEvents"
import useEnhancedEcommerceEvents from "@hooks/useEnhancedEcommerceEvents"
import useMaestraEvents from "@hooks/useMaestraEvents"
import useMultiWarehouseInventory from "@hooks/useMultiWarehouseInventory"
import useScrollHandler from "@hooks/useScrollHandler"
import { RootState } from "@redux/store"
import { assignPriorityBasedOnProductName } from "@utils/assignPriorityBasedOnProductName"
import { assignSubtypePriority } from "@utils/assignSubtypePriority"
import getAvailabilityByRegion from "@utils/getAvailabilityByRegion"
import {
  formatContentfulVariant,
  getCollectionBannerData,
  getIn,
  isNilOrEmpty,
} from "@utils/index"
import { DesktopOnly, MobileAndTabletOnly } from "@utils/MediaQuery"
import { getLocalStorage, writeLocalStorage } from "@utils/storage"
import atob from "atob"
import * as R from "ramda"
import React from "react"
import { connect } from "react-redux"
import styled, { css } from "styled-components"

import Banner, { BannerTypes } from "./components/Banner"
import FilterBar from "./components/FilterBar"
import SideNav from "./components/SideNav"
import {
  getBrands,
  getColors,
  getDeviceNameSlugMap,
  getDevices,
  getDiscountTiers,
  getSeries,
  getSubTypes,
  hasBrand,
  hasColor,
  hasDiscounted,
  hasDiscountTier,
  hasSeries,
  hasStock,
  hasSubType,
  sortSeries,
} from "./utils"

const Content = styled.div`
  display: flex;
  flex-direction: column;

  @media ${breakpoints.laptop} {
    flex-direction: row;
    border-top: solid 1px ${colors.dividerLineGray};
  }
`

const ProductList = styled.div<{ displayBanner: boolean }>`
  ${({ displayBanner }) => css`
    display: flex;
    margin-bottom: 86px;
    padding: 0;
    flex-wrap: wrap;
    justify-content: space-between;
    border-bottom: solid 1px ${colors.dividerLineGray};

    @media ${breakpoints.tablet} {
      margin-bottom: 0;
      padding: 0 calc(6 / 9 / 12 * 100vw) 124px;
      align-content: flex-start;
      justify-content: space-between;
      border: none;
    }

    @media ${breakpoints.laptop} {
      width: calc(10 / 12 * 100vw);
      max-width: calc(100vw - 300px);
      min-height: 80vh;
      margin-bottom: 0;
      padding: ${displayBanner
        ? "51px calc(1 / 12 * 100vw) 180px"
        : "113px calc(1 / 12 * 100vw) 180px"};
      align-content: flex-start;
      border: none;
    }

    ${ProductBlockContainer} {
      width: calc(50% - 0.5px);
      flex-grow: 0;
      flex-shrink: 0;
      flex-wrap: wrap;
      justify-content: space-between;
      border-top: solid 1px ${colors.dividerLineGray};

      @media ${breakpoints.tablet} {
        width: calc(50% - (3 / 9 / 12 * 100vw));
        margin-bottom: 48px;
        padding: 0;
        border: none;
      }

      @media ${breakpoints.laptop} {
        width: calc(100% / 3 - 24px);
        margin: 0 24px 46px 0;
        padding: 0;
        border: none;
      }
    }
  `}
`

const Row = styled.div<{ frosmoMobilePadding?: boolean; noPadding?: boolean }>`
  ${({ frosmoMobilePadding, noPadding }) => css`
    width: 100%;
    display: flex;
    padding: ${noPadding && frosmoMobilePadding
      ? "0 calc(1 / 8 * 100%) 0 20px"
      : noPadding && !frosmoMobilePadding
        ? "40px calc(1 / 8 * 100%) 0 20px"
        : frosmoMobilePadding
          ? "0 0 40px 20px"
          : "0 calc(1 / 8 * 100%) 40px 20px"};
    align-items: center;
    justify-content: space-between;

    @media ${breakpoints.tablet} {
      padding: 0 0 48px 0;
    }

    @media ${breakpoints.laptop} {
      margin-bottom: 40px;
      padding: 0 24px 0 0;
    }

    ${SortContainer} {
      @media ${breakpoints.tablet} {
        transform: translateX(28px);
      }

      @media ${breakpoints.laptop} {
        transform: translateX(28px);
      }
    }
  `}
`

const Header = styled.h1<{ displayBanner: boolean }>`
  ${({ displayBanner }) => css`
    ${h4Styles}
    display: ${displayBanner ? "none" : "inline-block"};
    max-width: 100%;
    flex-shrink: 0;

    @media ${breakpoints.tablet} {
      display: inline-block;
      max-width: calc(50% - (6 / 9 / 12 * 100vw));
    }

    @media ${breakpoints.laptop} {
      display: inline-block;
    }
  `}
`

const Subheader = styled.h1`
  ${smallcapsBold13Styles}
  display: inline-block;
  max-width: 100%;
  flex-shrink: 0;

  @media ${breakpoints.tablet} {
    max-width: calc(50% - (6 / 9 / 12 * 100vw));
  }
`

const Placeholder = styled.div`
  display: none;

  @media ${breakpoints.laptop} {
    display: flex;
    width: calc(100% / 3 - 24px);
    margin: 0 24px 46px 0;
    padding: 0;
  }
`

const Divider = styled.div`
  width: 1px;
  background-color: ${colors.dividerLineGray};

  @media ${breakpoints.tablet} {
    display: none;
  }

  @media ${breakpoints.laptop} {
    display: none;
  }
`

const NoMatchWrapper = styled.div`
  height: 100%;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;

  padding-top: 50px;

  > button {
    max-width: 300px;
    width: max-content;
    margin-top: 26px;
  }
`

const H5 = styled.h5`
  ${h5Styles}
`

const Recommendations = styled.div`
  max-width: 100%;

  @media ${breakpoints.laptop} {
    width: calc(100% + 8.33vw + 24px);
    max-width: unset;
  }
`

const Footer = styled.div`
  @media ${breakpoints.laptop} {
    margin-bottom: 96px;
    padding-left: calc(2 / 12 * 100vw);
  }

  p {
    font-size: 16px !important;
    line-height: 24px !important;
  }

  @media ${breakpoints.tablet} {
    p {
      font-size: 16px !important;
      line-height: 24px !important;
    }
  }

  @media ${breakpoints.laptop} {
    p {
      font-size: 16px !important;
      line-height: 24px !important;
    }
  }

  @media ${breakpoints.desktop} {
    p {
      font-size: 16px !important;
      line-height: 24px !important;
    }
  }
`

const formatFilter = (arr, filters, handler) =>
  arr.map((obj) => ({
    ...obj,
    onClick: () => handler(obj),
    toggled: filters && R.includes(obj, filters),
  }))

const toggleFilter = (key, filter, filters = []) =>
  filters.length > 0 && R.includes(filter, filters)
    ? R.filter((filterObj: any) => filterObj[key] !== filter[key])
    : [...filters, filter]

const priceSort = (a, b) => {
  const priceA = getIn(a, "variants.0.price", 0)
  const priceB = getIn(b, "variants.0.price", 0)
  return priceA - priceB
}

const priceSortReversed = (a, b) => {
  const priceA = getIn(a, "variants.0.price", 0)
  const priceB = getIn(b, "variants.0.price", 0)
  return priceB - priceA
}

const newestSort = (a, b) => {
  const aDate = new Date(getIn(a, "createdAt", 0))
  const bDate = new Date(getIn(b, "createdAt", 0))
  return (
    (((bDate as any) > (aDate as any)) as any) -
    (((bDate as any) < (aDate as any)) as any)
  )
}

// @ts-ignore
const bestSellingSort = R.descend(R.prop("priority"))
// @ts-ignore
const productSubTypeSort = R.descend(R.prop("subTypePriority"))

export enum SortMethods {
  ByBestSelling = "BEST_SELLING",
  ByFeatured = "BY_FEATURED",
  ByPrice = "BY_PRICE",
  ByPriceReversed = "BY_PRICE_REVERSED",
  ByRecent = "BY_RECENT",
}

const productListRef = React.createRef<HTMLDivElement>()
const emptyProductReference = []

const ProductsPage: any = ({
  automaticDiscounts,
  customerCountryCode,
  customerEmail,
  customerLocationFetched,
  data,
  location,
  pageContext,
}): any => {
  const { onClickProductCard, onViewProductList } = useEnhancedEcommerceEvents()
  const { elevarOnSelectItem, elevarOnViewItemList } = useElevarEvents()
  const { maestraOnViewCategory } = useMaestraEvents()

  const pinnedProducts: any = R.reverse(
    data?.contentfulGlobalSettings?.pinnedProducts || []
  )

  const allProducts: Array<any> = getIn(
    data,
    "allContentfulProduct.nodes",
    emptyProductReference
  )
  const allVariantSkus = React.useMemo(() => {
    const allVariants = allProducts.reduce((acc, product) => {
      const variants = getIn(product, "variants", [])
      const validVariants = variants.filter((variant: any) => {
        if (product.customPrint) {
          return (
            variant.deviceSlug &&
            variant.deviceName &&
            variant.images?.length > 0
          )
        }
        return variant.images?.length > 0
      })
      return [...acc, ...validVariants]
    }, [])

    return allVariants.map(({ sku }) => sku)
  }, [allProducts])

  const { inventoryData } = useMultiWarehouseInventory(allVariantSkus)

  const formattedProducts = React.useMemo(() => {
    return R.pipe(
      (products) => {
        return products
          .filter(
            (product) => !!product.variants && product.variants.length > 0
          )
          .map((product, i) => {
            const shopifyProductId = getIn(
              product,
              "shopifyProduct.shopifyProductId",
              ""
            )

            const allVariants = getIn(product, "variants", [])
            let variants = []

            if (!!product.customPrint && pageContext.deviceSlug) {
              variants = pageContext.deviceSlug
                ? allVariants.filter(
                    ({ device }) =>
                      device && device.slug === pageContext.deviceSlug
                  )
                : allVariants
            } else {
              variants = getIn(product, "variants", [])
            }

            const formattedVariants = variants.map((variant) => {
              const formattedVariant: any = formatContentfulVariant(variant)

              if (
                !product.customPrint &&
                customerLocationFetched &&
                customerCountryCode &&
                inventoryData
              ) {
                const isAvailable = getAvailabilityByRegion(
                  customerCountryCode,
                  formattedVariant.sku,
                  inventoryData
                ).isAvailable

                formattedVariant.isUnavailable = !isAvailable
              }

              return formattedVariant
            })

            let multipleDevices
            if (product.devices) {
              if (pageContext.deviceSlug) {
                const selectedDevice = product.devices.find(
                  ({ slug }) => slug === pageContext.deviceSlug
                )
                multipleDevices = selectedDevice?.name || ""
              } else {
                multipleDevices = product.devices.reduce((acc, curr) => {
                  if (acc) {
                    acc = acc + `, ${curr.name}`
                  } else {
                    acc = curr.name
                  }

                  return acc
                }, undefined)
              }
            }

            const gtmProduct = {
              brand: product.brand.name,
              device: multipleDevices ? multipleDevices : product.device.name,
              deviceType: product.device.type.name,
              list: `Collection: ${
                pageContext.seoHeader ||
                pageContext.seoTitle ||
                pageContext.title
              }`,
              price: product.variants[0].price,
              productId: shopifyProductId,
              productName: product.productName,
              sku: product.variants[0].sku,
              variantName: product.variants[0].name,
            }

            let list = ""

            if (typeof window !== "undefined") {
              list = getIn(window, "location.pathname")
            }

            const elevarProduct = {
              brand: "Urban Armor Gear",
              device: multipleDevices ? multipleDevices : product.device.name,
              deviceType: product.device.type.name,
              list,
              position: i + 1,
              price: product.variants[0].price,
              productId: shopifyProductId,
              productName: product.productName,
              sku: product.variants[0].sku,
              variantId: product.variants[0].shopifyVariantId,
              variantName: product.variants[0].name,
            }

            let discountTag
            let encodedProductId
            try {
              encodedProductId = atob(shopifyProductId)
            } catch (error) {
              encodedProductId = shopifyProductId
            }

            if (automaticDiscounts?.length) {
              const xIndex = automaticDiscounts.findIndex(
                ({ customerBuysProducts }) =>
                  customerBuysProducts?.indexOf(encodedProductId) > -1
              )
              const yIndex = automaticDiscounts.findIndex(
                ({ customerGetsProducts }) =>
                  customerGetsProducts?.indexOf(encodedProductId) > -1
              )

              let discountConfig
              if (xIndex > -1 || yIndex > -1) {
                const discountIndex = xIndex > -1 ? xIndex : yIndex
                const discountId = automaticDiscounts[discountIndex].id
                const parsedDiscountId = discountId.replace(
                  "gid://shopify/DiscountAutomaticNode/",
                  ""
                )
                discountConfig = data?.globalData?.automaticDiscounts?.find(
                  ({ discountId }) => discountId === parsedDiscountId
                )
              }

              let tagData
              if (discountConfig) {
                if (xIndex > -1) {
                  tagData = discountConfig.tagX
                } else if (yIndex > -1) {
                  tagData = discountConfig.tagY
                }
              }

              if (tagData) {
                // hide discountTag for now
                // discountTag = {
                //   text: tagData.text,
                //   mobileText: tagData.mobileText,
                //   color: tagData.color,
                //   backgroundColor: tagData.backgroundColor,
                // }
              }
            }

            const tags =
              getIn(product, "tags") || getIn(product, "series.tags") || []
            const formattedTags = tags.map(({ icon, ...rest }) => ({
              icon: icon?.file?.url,
              ...rest,
            }))

            return {
              ...R.pick([
                "createdAt",
                "customPrint",
                "bundleProduct",
                "slug",
                "productName",
                "brand",
                "device",
                "devices",
                "builtInMagnet",
                "series",
              ])(product),
              elevarProduct,
              gtmProduct,
              images: getIn(product, "images", []).map(
                (image) => image?.file?.url
              ),
              onClick: () => {
                const selectedProducts =
                  getLocalStorage(SELECTED_PRODUCTS_KEY) || {}
                writeLocalStorage(SELECTED_PRODUCTS_KEY, {
                  ...selectedProducts,
                  [elevarProduct.productId]: elevarProduct.list,
                })

                onClickProductCard(gtmProduct)
                elevarOnSelectItem(elevarProduct, elevarProduct.list)
              },
              productSubType: getIn(product, "productSubType.name"),
              shopifyProductId: getIn(
                product,
                "shopifyProduct.shopifyProductId"
              ),
              tags: discountTag
                ? [discountTag, ...formattedTags]
                : formattedTags,
              variants: formattedVariants,
            }
          })
      },
      // Manually assigning priority value based on product name. Ideally value would come from Contentful field
      R.map(assignPriorityBasedOnProductName),
      R.map(assignSubtypePriority)
    )(allProducts)
  }, [
    allProducts,
    inventoryData,
    automaticDiscounts,
    customerLocationFetched,
    customerCountryCode,
  ])

  const [showPrintedDesigns, setShowPrintedDesigns] = React.useState(false)
  const togglePrintedDesigns = () => {
    setShowPrintedDesigns(!showPrintedDesigns)
  }

  const [showBundleProducts, setShowBundleProducts] = React.useState(false)
  const toggleBundleProducts = () => {
    setShowBundleProducts(!showBundleProducts)
  }

  const hasBundleProducts = React.useMemo(() => {
    return formattedProducts.some((product) => Boolean(product.bundleProduct))
  }, [formattedProducts])

  const [showMagnetCompatibleProducts, setShowMagnetCompatibleProducts] =
    React.useState(false)
  const toggleShowMagnetCompatibleProducts = () => {
    setShowMagnetCompatibleProducts(!showMagnetCompatibleProducts)
  }

  const hasMagnetCompatibleProducts = React.useMemo(() => {
    return formattedProducts.some((product) => Boolean(product.builtInMagnet))
  }, [formattedProducts])

  const subTypes = React.useMemo(
    () => getSubTypes(formattedProducts as any),
    [formattedProducts]
  )

  const [subTypeFilters, setSubTypeFilters] = React.useState<any>([])
  const toggleSubType = (subType) => {
    setSubTypeFilters(toggleFilter("label", subType, subTypeFilters))
  }

  const formattedSubTypes = React.useMemo(
    () => formatFilter(subTypes, subTypeFilters, toggleSubType),
    [subTypes, subTypeFilters, toggleSubType]
  )

  const colors = React.useMemo(
    () =>
      getColors(
        formattedProducts.filter((product) => !product.customPrint) as any
      ),
    [formattedProducts]
  )

  const [colorFilters, setColorFilters] = React.useState<any>([])
  const toggleColor = (color) => {
    setColorFilters(toggleFilter("label", color, colorFilters))
  }

  const formattedColors = React.useMemo(
    () => formatFilter(colors, colorFilters, toggleColor),
    [colors, colorFilters, toggleColor]
  )

  const brands = React.useMemo(
    () =>
      getBrands(
        formattedProducts as any,
        getIn(data, "contentfulGlobalSettings.menuProductBrandOrder", [])
      ),
    [formattedProducts]
  )

  const [brandFilters, setBrandFilters] = React.useState<any>([])
  const toggleBrand = (brand) => {
    setBrandFilters(toggleFilter("label", brand, brandFilters))
  }

  const formattedBrands = React.useMemo(
    () => formatFilter(brands, brandFilters, toggleBrand),
    [brands, brandFilters, toggleBrand]
  )

  const series = React.useMemo(() => {
    const productSeries = getSeries(formattedProducts as any)
    const seriesOrder = getIn(
      data,
      "contentfulGlobalSettings.menuProductSeriesOrder",
      []
    )
    return sortSeries(productSeries, R.map(R.prop("name"))(seriesOrder))
  }, [formattedProducts])

  const [seriesFilters, setSeriesFilters] = React.useState<any>([])
  const toggleSeries = (series) => {
    setSeriesFilters(toggleFilter("label", series, seriesFilters))
  }

  const formattedSeries = React.useMemo(
    () => formatFilter(series, seriesFilters, toggleSeries),
    [series, seriesFilters, toggleSeries]
  )

  const discountTiers = React.useMemo(() => {
    const discountFilters = getIn(
      data,
      "contentfulClearanceSettings.clearanceFilters",
      []
    ).map((d) => parseInt(d) / 5)
    const productDiscountTiers =
      getDiscountTiers(formattedProducts as any) || []
    return Object.keys(productDiscountTiers).reduce((tiers, tier) => {
      if (discountFilters.includes(parseInt(tier))) {
        tiers[tier] = productDiscountTiers[tier]
      }
      return tiers
    }, {})
  }, [data.contentfulClearanceSettings, formattedProducts])

  const [discountTierFilters, setDiscountTierFilters] = React.useState<any>([])
  const toggleDiscountTier = (tier) => {
    if (discountTierFilters[0] === tier) {
      setDiscountTierFilters([])
    } else {
      setDiscountTierFilters([tier])
    }
  }

  const formatDiscountTierLabel = (tier) => {
    const tiersCounts = Object.keys(discountTiers).filter(
      (t) => parseInt(t) >= parseInt(tier)
    )
    const totalCount = tiersCounts.reduce(
      (acc, curr) => acc + discountTiers[curr],
      0
    )

    if (tier === "0") {
      return `1% and more (${totalCount})`
    } else {
      return `${parseInt(tier) * 5}% and more (${totalCount})`
    }
  }

  const formattedDiscountTiers = React.useMemo(() => {
    const validDiscountTiers = R.omit(["-1"])(discountTiers)
    return Object.keys(validDiscountTiers).map(
      (tier) =>
        ({
          label: formatDiscountTierLabel(tier),
          onClick: () => {
            toggleDiscountTier(tier)
          },
          toggled: discountTierFilters && R.includes(tier, discountTierFilters),
        }) as any
    )
  }, [discountTiers, discountTierFilters, toggleDiscountTier])

  const customPrintProducts = React.useMemo(() => {
    return formattedProducts.filter((product) => !!product.customPrint)
  }, [formattedProducts])

  const deviceNameSlugMap = React.useMemo(
    () => getDeviceNameSlugMap(customPrintProducts as any),
    [customPrintProducts]
  )

  const devices = React.useMemo(
    () => getDevices(customPrintProducts as any),
    [customPrintProducts]
  )

  const [devicesFilter, setDevicesFilter] = React.useState<any>([])
  const toggleDevices = (device) => {
    if (devicesFilter.length > 0 && devicesFilter[0].label === device.label) {
      setDevicesFilter([])
    } else {
      setDevicesFilter([device])
    }
  }

  const formattedDevices = React.useMemo(
    () => formatFilter(devices, devicesFilter, toggleDevices),
    [devices, devicesFilter, toggleDevices]
  )

  const [showClearance, setShowClearance] = React.useState(false)
  const toggleClearance = () => {
    setShowClearance(!showClearance)
  }

  const [showInStock, setShowInStock] = React.useState(false)
  const toggleInStock = () => {
    setShowInStock(!showInStock)
  }

  const [showFilterMenu, setShowFilterMenu] = React.useState(false)
  const openFilterMenu = (e) => {
    e.stopPropagation()
    setShowFilterMenu(true)
  }
  const closeFilterMenu = () => {
    setShowFilterMenu(false)
  }

  const filterByPrintedDesign = R.filter((p: any) => !!p.customPrint)
  const filterByBundleProducts = R.filter((p: any) => !!p.bundleProduct)
  const filterByMagnetCompatible = R.filter((p: any) =>
    Boolean(p.builtInMagnet)
  )
  const filterBySubType = R.filter((p) => hasSubType(p, subTypeFilters))
  const filterByColor = R.filter((p) => hasColor(p, colorFilters))
  const filterByBrand = R.filter((p) => hasBrand(p, brandFilters))
  const filterBySeries = R.filter((p) => hasSeries(p, seriesFilters))
  const filterByDiscountTier = R.filter((p) =>
    hasDiscountTier(p, discountTierFilters)
  )
  const filterByDevice = R.filter((p: any) => {
    return !!p?.variants?.find(
      ({ deviceName }) => deviceName === devicesFilter[0].label
    )
  })

  const countryProhibitedProducts = getIn(
    data,
    "contentfulGlobalSettings.countryProhibitedProducts",
    []
  )
  const [notAvailableInCustomerCountry, setNotAvailableInCustomerCountry] =
    React.useState<Array<string>>([])

  React.useEffect(() => {
    if (
      customerLocationFetched &&
      customerCountryCode &&
      countryProhibitedProducts.length > 0
    ) {
      const prohibitedProducts: Array<string> = []
      countryProhibitedProducts.forEach((rule) => {
        const countries = getIn(rule, "countries", [])
        const products = getIn(rule, "products", [])

        if (countries.includes(customerCountryCode)) {
          products.forEach((product) => {
            prohibitedProducts.push(product.slug)
          })
        }
      })

      setNotAvailableInCustomerCountry(prohibitedProducts)
    }
  }, [countryProhibitedProducts, customerLocationFetched, customerCountryCode])

  const [availablePrintedDesignVariants, setAvailablePrintedDesignVariants] =
    React.useState<any>([])
  const [
    unavailablePrintedDesignVariants,
    setUnavailablePrintedDesignVariants,
  ] = React.useState<any>([])

  const AVAILABILITY_US = "Sell in US"
  const AVAILABILITY_INTL = "Sell INTL"
  const AVAILABILITY_EVERYWHERE = "Sell Everywhere"

  React.useEffect(() => {
    if (customerLocationFetched && customerCountryCode) {
      const availableSkus: any = []
      const unavailableSkus: any = []

      formattedProducts.forEach((product) => {
        if (product.customPrint) {
          product.variants.forEach((variant) => {
            if (customerCountryCode === "US") {
              if (
                variant.availability === AVAILABILITY_US ||
                variant.availability === AVAILABILITY_EVERYWHERE
              ) {
                availableSkus.push(variant.sku)
              } else {
                unavailableSkus.push(variant.sku)
              }
            } else {
              if (
                variant.availability === AVAILABILITY_INTL ||
                variant.availability === AVAILABILITY_EVERYWHERE
              ) {
                availableSkus.push(variant.sku)
              } else {
                unavailableSkus.push(variant.sku)
              }
            }
          })
        }
      })

      setAvailablePrintedDesignVariants(availableSkus)
      setUnavailablePrintedDesignVariants(unavailableSkus)
    }
  }, [formattedProducts, customerLocationFetched, customerCountryCode])

  const filterProducts: any = (products) => {
    const withoutProhibitedProducts = products.filter(
      (product) => !notAvailableInCustomerCountry.includes(product.slug)
    )

    const [
      allProducts,
      availablePrintedDesignProducts,
      unavailablePrintedDesignProducts,
    ] = withoutProhibitedProducts.reduce(
      (result, product) => {
        if (product.customPrint) {
          const availableVariants: any = []
          product.variants.forEach((variant) => {
            if (availablePrintedDesignVariants.includes(variant.sku)) {
              availableVariants.push(variant)
            }
          })

          if (availableVariants.length > 0) {
            result[0].push(product)
            result[1].push({
              ...product,
              variants: availableVariants,
            })
          } else {
            result[2].push(product)
          }
        } else {
          result[0].push(product)
        }

        return result
      },
      [[], [], []]
    )

    if (
      showPrintedDesigns &&
      !subTypeFilters.length &&
      !colorFilters.length &&
      !seriesFilters.length &&
      !showClearance &&
      !showInStock
    ) {
      return availablePrintedDesignProducts
    }

    const shownPrintedDesigns = showPrintedDesigns
      ? availablePrintedDesignProducts
      : []

    return [
      ...shownPrintedDesigns,
      // @ts-ignore
      ...R.pipe(
        R.unless(() => !showBundleProducts, filterByBundleProducts),
        R.unless(() => !showMagnetCompatibleProducts, filterByMagnetCompatible),
        R.unless(() => isNilOrEmpty(subTypeFilters), filterBySubType),
        R.unless(() => isNilOrEmpty(colorFilters), filterByColor),
        R.unless(() => isNilOrEmpty(brandFilters), filterByBrand),
        R.unless(() => isNilOrEmpty(seriesFilters), filterBySeries),
        R.unless(() => isNilOrEmpty(discountTierFilters), filterByDiscountTier),
        R.unless(() => isNilOrEmpty(devicesFilter), filterByDevice),
        R.unless(() => !showClearance, R.filter(hasDiscounted)),
        R.unless(() => !showInStock, R.filter(hasStock))
      )(allProducts),
    ]
  }

  const isCustomCollection = !!data?.filterData?.filters?.collection
  const isWatchCollection =
    pageContext?.title === "Watch" ||
    pageContext?.title === "Apple Watch" ||
    pageContext?.title === "Apple Watch Ultra" ||
    pageContext?.title === "Apple Watches"
  const isS24Collection =
    pageContext?.title === "Samsung Phones" ||
    pageContext?.title === "Galaxy S24" ||
    pageContext?.title === "Galaxy S24 Plus" ||
    pageContext?.title === "Galaxy S24 Ultra"
  const isGearCollection = pageContext?.title === "Gear"
  const [sortMethod, setSortMethod] = React.useState<any>(
    isCustomCollection ||
      isWatchCollection ||
      isS24Collection ||
      isGearCollection
      ? SortMethods.ByFeatured
      : SortMethods.ByRecent
  )

  const dropdownHandler = (sortMethod) => {
    setSortMethod(sortMethod)
  }

  const dropdownOptions = {
    isCustomCollection,
    options: [
      { name: "Featured", sort: SortMethods.ByFeatured },
      { name: "Price Low to High", sort: SortMethods.ByPrice },
      { name: "Price High to Low", sort: SortMethods.ByPriceReversed },
      { name: "Newest", sort: SortMethods.ByRecent },
      { name: "Best Selling", sort: SortMethods.ByBestSelling },
    ],
    setSort: dropdownHandler,
    sortMethod,
  }

  const pinnedProductSort = React.useMemo(() => {
    return (a, b) => {
      const indexA = pinnedProducts.findIndex(({ slug }) => slug === a.slug)
      const indexB = pinnedProducts.findIndex(({ slug }) => slug === b.slug)

      return indexB - indexA
    }
  }, [pinnedProducts])

  const sortMethodsMap = {
    [SortMethods.ByBestSelling]: [bestSellingSort, pinnedProductSort],
    [SortMethods.ByFeatured]: pinnedProductSort,
    [SortMethods.ByPrice]: [priceSort, pinnedProductSort],
    [SortMethods.ByPriceReversed]: [priceSortReversed, pinnedProductSort],
    [SortMethods.ByRecent]: [newestSort, productSubTypeSort, pinnedProductSort],
  }

  const [renderedProductCount, setRenderedProductCount] = React.useState(18)

  const onScrollHandler = () => {
    if (
      typeof window === "undefined" ||
      !productListRef ||
      !productListRef.current ||
      formattedProducts.length <= renderedProductCount
    ) {
      return
    }

    const productListNodes = productListRef.current.childNodes
    const positionReference: any = productListNodes[productListNodes.length - 3]

    if (positionReference.getBoundingClientRect().top < window.innerHeight) {
      setRenderedProductCount(renderedProductCount + 18)
    }
  }

  useScrollHandler({
    onScroll: onScrollHandler,
    runForProps: [renderedProductCount],
  })

  const [renderedProducts, setRenderedProducts] = React.useState(
    R.take(renderedProductCount, filterProducts(formattedProducts))
  )

  React.useEffect(() => {
    const filteredProducts = filterProducts(formattedProducts)
    const mappedSortMethod = sortMethod ? sortMethodsMap[sortMethod] : null
    let sortedProducts

    if (Array.isArray(mappedSortMethod)) {
      const mappedSortFns: any = mappedSortMethod.map((fn) => R.sort(fn))

      sortedProducts = R.pipe(
        // @ts-ignore
        ...mappedSortFns
      )(filteredProducts)
    } else {
      sortedProducts = mappedSortMethod
        ? R.sort(mappedSortMethod, filteredProducts)
        : filteredProducts
    }

    setRenderedProducts(R.take(renderedProductCount, sortedProducts))
  }, [
    customerLocationFetched,
    customerCountryCode,
    formattedProducts,
    availablePrintedDesignVariants,
    unavailablePrintedDesignVariants,
    showPrintedDesigns,
    showBundleProducts,
    showMagnetCompatibleProducts,
    subTypeFilters,
    colorFilters,
    brandFilters,
    seriesFilters,
    discountTierFilters,
    devicesFilter,
    showClearance,
    showInStock,
    sortMethod,
    renderedProductCount,
  ])

  const [onViewProductListCalled, setOnViewProductListCalled] =
    React.useState<boolean>(false)

  React.useEffect(() => {
    if (!onViewProductListCalled && formattedProducts[0]) {
      const gtmProducts = formattedProducts.map(({ gtmProduct }) => gtmProduct)
      const elevarProducts = formattedProducts.map(
        ({ elevarProduct }) => elevarProduct
      )
      onViewProductList(gtmProducts)
      elevarOnViewItemList(elevarProducts)
      maestraOnViewCategory({
        category: location.pathname,
        user: { email: customerEmail },
      })
      setOnViewProductListCalled(true)
    }
  }, [formattedProducts])

  const filterCount =
    (showPrintedDesigns ? 1 : 0) +
    (showBundleProducts ? 1 : 0) +
    (showMagnetCompatibleProducts ? 1 : 0) +
    subTypeFilters.length +
    colorFilters.length +
    brandFilters.length +
    seriesFilters.length +
    discountTierFilters.length +
    (showClearance ? 1 : 0) +
    (showInStock ? 1 : 0)

  const resetFilters = () => {
    setShowPrintedDesigns(false)
    setShowMagnetCompatibleProducts(false)
    setSubTypeFilters([])
    setColorFilters([])
    setSeriesFilters([])
    setShowInStock(false)
    setShowClearance(false)
  }

  const seoData = {
    description: pageContext.seoDescription || pageContext.description,
    keywords: pageContext.seoKeywords,
    title: pageContext.seoTitle || pageContext.title,
  }

  let footerContentForDeviceType

  if (pageContext.brandSlug && pageContext.deviceTypeSlug) {
    const allCollectionFooters = getIn(
      data,
      "contentfulBrand.collectionFooters",
      []
    )
    footerContentForDeviceType = allCollectionFooters.find((textBlock) => {
      const referenceSlug = getIn(textBlock, "reference.slug")
      return referenceSlug === pageContext.deviceTypeSlug
    })
  }

  const footerContent =
    footerContentForDeviceType ||
    getIn(data, "contentfulDevice.collectionFooter") ||
    getIn(data, "contentfulDeviceType.collectionFooter") ||
    getIn(data, "contentfulProductSeries.collectionFooter") ||
    getIn(data, "footerContent")
  const footerContentWithLocation = footerContent && {
    ...footerContent,
    location,
  }

  const bannerContentKey = pageContext?.collectionBannerKey
  const bannerContentObj = bannerContentKey ? getIn(data, bannerContentKey) : {}
  const bannerContentData = getCollectionBannerData(bannerContentObj)
  let displayBanner = true

  if (
    !bannerContentData.collectionBannerType ||
    !bannerContentData.bannerImage ||
    !bannerContentData.mobileBannerImage
  ) {
    displayBanner = false
  } else if (
    bannerContentData.collectionBannerType === BannerTypes.HEADER &&
    !bannerContentData.bannerHeader
  ) {
    displayBanner = false
  } else if (
    bannerContentData.collectionBannerType === BannerTypes.PARAGRAPH &&
    (!bannerContentData.paragraphHeader || !bannerContentData.paragraphBody)
  ) {
    displayBanner = false
  }

  return (
    <Layout data={data} location={location}>
      <Helmet {...seoData} />
      <DesktopOnly>
        <Banner {...bannerContentData} />
      </DesktopOnly>
      <Content>
        <MobileAndTabletOnly>
          <ProductFilterMobile
            activeBrands={brandFilters.length}
            activeColors={colorFilters.length}
            activeDiscountTiers={discountTierFilters.length}
            activeProductSeries={seriesFilters.length}
            activeSubTypes={subTypeFilters.length}
            brands={formattedBrands}
            colors={formattedColors}
            devices={formattedDevices}
            discountTiers={formattedDiscountTiers}
            hasBundleProducts={hasBundleProducts}
            hasMagnetCompatibleProducts={hasMagnetCompatibleProducts}
            hasPrintedDesigns={customPrintProducts.length > 0}
            isClearance={!!pageContext.clearance}
            isCustomPrints={!!pageContext.customPrints}
            isOpen={showFilterMenu}
            onRequestClose={closeFilterMenu}
            onResetFilters={() => resetFilters()}
            onToggleBundleProducts={toggleBundleProducts}
            onToggleClearance={toggleClearance}
            onToggleInStock={toggleInStock}
            onToggleMagnetCompatibleProducts={
              toggleShowMagnetCompatibleProducts
            }
            onTogglePrintedDesigns={togglePrintedDesigns}
            productSeries={formattedSeries}
            showBundleProducts={showBundleProducts}
            showClearance={showClearance}
            showInStock={showInStock}
            showMagnetCompatibleProducts={showMagnetCompatibleProducts}
            showPrintedDesigns={showPrintedDesigns}
            subTypes={formattedSubTypes}
          />
          <FilterBar
            filterCount={
              subTypeFilters.length +
              colorFilters.length +
              brandFilters.length +
              seriesFilters.length +
              discountTierFilters.length
            }
            filters={getIn(data, "menuData.filters", {})}
            isClearance={!!pageContext.clearance}
            isCustomPrints={!!pageContext.customPrints}
            onFilterClick={openFilterMenu}
          />
          <Banner {...bannerContentData} />
        </MobileAndTabletOnly>
        <DesktopOnly>
          <SideNav
            activeBrands={brandFilters.length}
            activeColors={colorFilters.length}
            activeDiscountTiers={discountTierFilters.length}
            activeProductSeries={seriesFilters.length}
            activeSubTypes={subTypeFilters.length}
            brands={formattedBrands}
            colors={formattedColors}
            devices={formattedDevices}
            discountTiers={formattedDiscountTiers}
            displayBanner={displayBanner}
            filterFunnel={getIn(data, "filterData.filterFunnel", {})}
            globalData={getIn(data, "globalData", {})}
            hasBundleProducts={hasBundleProducts}
            hasMagnetCompatibleProducts={hasMagnetCompatibleProducts}
            hasPrintedDesigns={customPrintProducts.length > 0}
            isClearance={!!pageContext.clearance}
            isCustomPrints={!!pageContext.customPrints}
            menuData={getIn(data, "menuData", {})}
            onResetFilters={() => resetFilters()}
            onToggleBundleProducts={toggleBundleProducts}
            onToggleClearance={toggleClearance}
            onToggleInStock={toggleInStock}
            onToggleMagnetCompatibleProducts={
              toggleShowMagnetCompatibleProducts
            }
            onTogglePrintedDesigns={togglePrintedDesigns}
            productSeries={formattedSeries}
            showBundleProducts={showBundleProducts}
            showClearance={showClearance}
            showInStock={showInStock}
            showMagnetCompatibleProducts={showMagnetCompatibleProducts}
            showPrintedDesigns={showPrintedDesigns}
            subTypes={formattedSubTypes}
          />
        </DesktopOnly>
        <ProductList displayBanner={displayBanner} ref={productListRef}>
          <Row noPadding={displayBanner}>
            <Header displayBanner={displayBanner}>
              {displayBanner ? "" : pageContext.seoHeader || pageContext.title}
            </Header>
            <SortDropdown {...dropdownOptions} />
          </Row>
          {R.isEmpty(renderedProducts) && (
            <NoMatchWrapper>
              <H5>No products match your filters</H5>
              <Button
                hideDash
                onClick={() => resetFilters()}
                text={`RESET FILTERS (${filterCount})`}
              />
            </NoMatchWrapper>
          )}
          {!pageContext.clearance && !R.isEmpty(renderedProducts) && (
            <>
              <Row frosmoMobilePadding noPadding={displayBanner}>
                <Recommendations
                  dangerouslySetInnerHTML={{
                    __html: `<div id="frosmo-category-reco-1"></div>`,
                  }}
                />
              </Row>
              <Row>
                <Subheader>All Results</Subheader>
              </Row>
            </>
          )}
          {renderedProducts.map((p: any, i) => (
            <React.Fragment key={p.slug}>
              <ProductBlockWithSwatches
                customPrint={p.customPrint}
                hideMobileSwatches
                images={p.images}
                onClick={p.onClick}
                productName={p.productName}
                selectedDeviceSlug={
                  pageContext.deviceSlug ||
                  (deviceNameSlugMap &&
                    devicesFilter[0] &&
                    deviceNameSlugMap[devicesFilter[0].label])
                }
                slug={p.slug}
                tags={p.tags}
                variants={p.variants}
              />
              {i % 2 === 0 && <Divider />}
            </React.Fragment>
          ))}
          {!R.isEmpty(renderedProducts) && renderedProducts.length % 3 == 2 && (
            <Placeholder />
          )}
          {!pageContext.clearance && !R.isEmpty(renderedProducts) && (
            <Row frosmoMobilePadding>
              <Recommendations
                dangerouslySetInnerHTML={{
                  __html: `<div id="frosmo-category-reco-2"></div>`,
                }}
              />
            </Row>
          )}
        </ProductList>
      </Content>
      {footerContentWithLocation && (
        <Footer>
          <ContentfulContent content={[footerContentWithLocation]} />
        </Footer>
      )}
    </Layout>
  )
}

const mapState = (state: RootState) => ({
  automaticDiscounts: state.discounts.automaticDiscounts,
  customerCountryCode: state.customer.customerLocation.customerCountryCode,
  customerEmail: state.customer.data?.email || "",
  customerLocationFetched: state.customer.customerLocationFetched,
})

export default connect(mapState)(ProductsPage)
